// src/lib/http/remoteBff.ts
// ────────────────────────────────────────────────────────────────────────────
// Thin HTTP client for talking to a *remote* BFF instance using a device
// Bearer token (obtained via RFC 8628 Device Authorization Grant).
//
// Unlike the local `bff` client which relies on session cookies, this
// client authenticates with `Authorization: Bearer <access_token>`.
// ────────────────────────────────────────────────────────────────────────────

export class RemoteBffError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'RemoteBffError';
    this.status = status;
    this.body = body;
  }
}

export interface RemoteBffOptions {
  /** Full origin of the remote BFF, e.g. `https://bff-xyz.a.run.app` */
  origin: string;
  /** Base path on the remote BFF, default `/bff` */
  basePath?: string;
  /** Device access token (Bearer) */
  accessToken: string;
  /** Request timeout in ms, default 30 000 */
  timeoutMs?: number;
}

function joinPath(a: string, b: string): string {
  return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
}

/**
 * Create a remote BFF client bound to a specific origin + access token.
 *
 * ```ts
 * const remote = createRemoteBffClient({
 *   origin: 'https://bff-dev.a.run.app',
 *   accessToken: '<device_jwt>',
 * });
 * await remote.post('/orchestrator/migrate/scopes/import', manifest);
 * ```
 */
export function createRemoteBffClient(opts: RemoteBffOptions) {
  const ORIGIN   = opts.origin;
  const BASEPATH = opts.basePath ?? '/bff';
  const TIMEOUT  = opts.timeoutMs ?? 30_000;
  const TOKEN    = opts.accessToken;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinPath(ORIGIN, joinPath(BASEPATH, path));
    const ctrl = new AbortController();
    // Explicit TimeoutError reason → fetch rejects with a readable
    // message instead of the browser-default "signal is aborted
    // without reason". Mirrors the modern `AbortSignal.timeout(ms)`
    // contract; callers can branch on `err.name === 'TimeoutError'`.
    const timer = setTimeout(
      () => ctrl.abort(new DOMException(`Remote BFF request timed out after ${TIMEOUT}ms`, 'TimeoutError')),
      TIMEOUT,
    );

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${TOKEN}`,
        },
        body: body != null ? JSON.stringify(body) : undefined,
        signal: ctrl.signal,
      });

      let parsed: unknown;
      const ct = res.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        parsed = await res.json();
      } else {
        parsed = await res.text();
      }

      if (!res.ok) {
        throw new RemoteBffError(
          `Remote BFF ${method} ${path} → ${res.status}`,
          res.status,
          parsed,
        );
      }

      return parsed as T;
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    get:  <T = unknown>(path: string) => request<T>('GET', path),
    post: <T = unknown>(path: string, body?: unknown) => request<T>('POST', path, body),
    put:  <T = unknown>(path: string, body?: unknown) => request<T>('PUT', path, body),
    del:  <T = unknown>(path: string) => request<T>('DELETE', path),
  };
}

export type RemoteBffClient = ReturnType<typeof createRemoteBffClient>;
